import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from '../locales/en/common.json';
import frCommon from '../locales/fr/common.json';

export interface CreateI18nOptions<TApp> {
  /**
   * Identifies the consuming app. Part of the public factory contract and the
   * unit of instance identity for app-scoped config/telemetry; not used to
   * branch translation behaviour today (both apps share the same `common` + `app`
   * namespace wiring).
   */
  app: 'admin' | 'portal';
  /** App namespace resources, injected by the app — the package never imports from apps. */
  appResources: { en: TApp; fr: TApp };
  initialLanguage?: 'en' | 'fr';
}

/**
 * Build an isolated i18next instance with the shared `common` namespace plus the
 * app's injected `app` namespace. Language persistence is the app's responsibility.
 */
export function createI18n<TApp extends Record<string, unknown>>(
  opts: CreateI18nOptions<TApp>,
): i18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    lng: opts.initialLanguage ?? 'en',
    fallbackLng: 'en',
    ns: ['common', 'app'],
    defaultNS: 'app',
    resources: {
      en: { common: enCommon, app: opts.appResources.en },
      fr: { common: frCommon, app: opts.appResources.fr },
    },
    interpolation: { escapeValue: false },
    initAsync: false,
    returnNull: false,
  });
  return instance;
}
